import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { ChatbotDefaultQuestions } from "@/types";

type RecommendedQuestions = string[];

type RelatedQuestionsProps = {
  questions: RecommendedQuestions | ChatbotDefaultQuestions;
  onQuestionSelect: (question: string) => void;
};

const RelatedQuestions: React.FC<RelatedQuestionsProps> = ({
  questions,
  onQuestionSelect,
}) => {
  const [openItem, setOpenItem] = useState<number | null>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const toggleItem = (id: number) => {
    setOpenItem((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (openItem !== null) {
      const el = itemRefs.current[openItem];
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    }
  }, [openItem]);

  const isGrouped = (
    q: RecommendedQuestions | ChatbotDefaultQuestions,
  ): q is ChatbotDefaultQuestions =>
    Array.isArray(q) &&
    typeof q[0] === "object" &&
    q[0] !== null &&
    "header" in q[0];

  return (
    <div className="mt-4 w-[90%]">
      {isGrouped(questions) ? (
        <div className="space-y-2">
          {questions.map((group) => {
            const isOpen = openItem === group.id;
            return (
              <div
                key={group.id}
                ref={(el) => (itemRefs.current[group.id] = el)}
                className="overflow-hidden rounded-lg border border-gray-300"
              >
                <button
                  onClick={() => toggleItem(group.id)}
                  className="group flex w-full items-center justify-between rounded-t-lg bg-gray-100 px-4 py-2 text-left hover:bg-gray-200"
                >
                  <span className="text-medium">{group.header}</span>
                  <ChevronDown
                    className={`size-4 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: 0.4,
                        ease: [0.4, 0, 0.2, 1],
                      }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 border-t border-gray-200 bg-white px-4 py-2">
                        {/* 2.1.1 Keyboard — A / 4.1.2 — A: suggested questions were
                            <div onClick>, so keyboard users could not choose any of them. */}
                        {group.data.map((question, idx) => (
                          <button
                            type="button"
                            key={idx}
                            onClick={() => onQuestionSelect(question)}
                            className="w-full cursor-pointer rounded-md border border-gray-300 px-3 py-2 text-left text-sm transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {/* 2.1.1 Keyboard — A / 4.1.2 — A: see the grouped list above. */}
          {questions.map((question, idx) => (
            <button
              type="button"
              key={idx}
              onClick={() => onQuestionSelect(question)}
              className="w-full cursor-pointer rounded-md border border-gray-300 px-3 py-2 text-left text-sm transition hover:bg-primary-extraLight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {question}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelatedQuestions;
